/**
 * useFavoriteArticles
 *
 * Article bookmark state. Backed by the `/me/article-interactions` API
 * (each row's `likedAt` field). No provider needed — TanStack Query
 * shares the underlying cache across consumers.
 */
import React, { useCallback, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  type ArticleInteraction,
  articleInteractionsKey,
  useArticleInteractions,
  useUpsertArticleInteraction,
} from "./useArticleInteractions";

/**
 * Pass-through provider kept for backwards compatibility (Olly's App.tsx
 * still mounts it). No state — TanStack Query is the source of truth.
 */
export function FavoritesProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
}

export function useFavoriteArticles() {
  const { data: interactions = [], isLoading } = useArticleInteractions();
  const { mutate } = useUpsertArticleInteraction();
  const qc = useQueryClient();

  const favorites = useMemo(
    () => interactions.filter((i) => i.likedAt != null).map((i) => i.articleId),
    [interactions],
  );

  const isFavorite = useCallback(
    (articleId: string) =>
      interactions.some((i) => i.articleId === articleId && i.likedAt != null),
    [interactions],
  );

  // Read the latest cache at call-time (not from a stale closure) so a
  // double-tap doesn't send the same likedAt value twice — the first tap's
  // onMutate has already flipped the optimistic state before the second
  // tap reads it.
  const toggleFavorite = useCallback(
    (articleId: string) => {
      const current =
        qc.getQueryData<ArticleInteraction[]>(articleInteractionsKey) ?? [];
      const existing = current.find((i) => i.articleId === articleId);
      const currentlyLiked = existing?.likedAt != null;
      mutate({
        articleId,
        body: {
          likedAt: currentlyLiked ? null : new Date().toISOString(),
        },
      });
    },
    [qc, mutate],
  );

  return { favorites, isFavorite, toggleFavorite, isLoading };
}
